import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { usePageTitle } from '@/components/layout/page-title';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { CheckoutTab } from './tabs/CheckoutTab';
import { ContactPersonsTab } from './tabs/ContactPersonsTab';
import { CustomPackagesTab } from './tabs/CustomPackagesTab';
import { GeneralTab } from './tabs/GeneralTab';
import { PickupLocationsTab } from './tabs/PickupLocationsTab';

type TabKey = 'general' | 'checkout' | 'custom-packages' | 'pickup-locations' | 'contact-persons';

const TAB_KEYS: TabKey[] = [
  'general',
  'checkout',
  'custom-packages',
  'pickup-locations',
  'contact-persons',
];

function parseTab(value: string | null): TabKey {
  return TAB_KEYS.includes(value as TabKey) ? (value as TabKey) : 'general';
}

export function SettingsPage() {
  const { t } = useTranslation('settings');
  const tCommon = useTranslation('common').t;
  usePageTitle(t('title'));
  const [params, setParams] = useSearchParams();
  const activeTab = parseTab(params.get('tab'));

  const dirtyRef = useRef<Record<TabKey, boolean>>({
    general: false,
    checkout: false,
    'custom-packages': false,
    'pickup-locations': false,
    'contact-persons': false,
  });
  const [pendingTab, setPendingTab] = useState<TabKey | null>(null);

  const setDirty = useCallback(
    (tab: TabKey) => (dirty: boolean) => {
      dirtyRef.current[tab] = dirty;
    },
    [],
  );

  const onTabChange = (next: string) => {
    const nextTab = parseTab(next);
    if (nextTab === activeTab) return;
    if (dirtyRef.current[activeTab]) {
      setPendingTab(nextTab);
      return;
    }
    setParams({ tab: nextTab }, { replace: true });
  };

  const discardAndSwitch = () => {
    if (!pendingTab) return;
    dirtyRef.current[activeTab] = false;
    setParams({ tab: pendingTab }, { replace: true });
    setPendingTab(null);
  };

  return (
    <div className="flex w-full flex-col gap-4">
      {/* Compact 24px title (prototype-settings-checkout.html). */}
      <h2 className="text-2xl font-semibold tracking-[-0.6px] text-black">{t('title')}</h2>

      <Tabs value={activeTab} onValueChange={onTabChange}>
        <TabsList className="mb-6">
          <TabsTrigger value="general">{t('tabs.general')}</TabsTrigger>
          <TabsTrigger value="checkout">{t('tabs.checkout')}</TabsTrigger>
          <TabsTrigger value="custom-packages">{t('tabs.customPackages')}</TabsTrigger>
          <TabsTrigger value="pickup-locations">{t('tabs.pickupLocations')}</TabsTrigger>
          <TabsTrigger value="contact-persons">{t('tabs.contactPersons')}</TabsTrigger>
        </TabsList>
        <TabsContent value="general" forceMount hidden={activeTab !== 'general'}>
          <GeneralTab dirtyRef={setDirty('general')} />
        </TabsContent>
        <TabsContent value="checkout" forceMount hidden={activeTab !== 'checkout'}>
          <CheckoutTab dirtyRef={setDirty('checkout')} />
        </TabsContent>
        <TabsContent value="custom-packages" forceMount hidden={activeTab !== 'custom-packages'}>
          <CustomPackagesTab dirtyRef={setDirty('custom-packages')} />
        </TabsContent>
        <TabsContent value="pickup-locations" forceMount hidden={activeTab !== 'pickup-locations'}>
          <PickupLocationsTab dirtyRef={setDirty('pickup-locations')} />
        </TabsContent>
        <TabsContent value="contact-persons" forceMount hidden={activeTab !== 'contact-persons'}>
          <ContactPersonsTab dirtyRef={setDirty('contact-persons')} />
        </TabsContent>
      </Tabs>

      <AlertDialog open={pendingTab !== null} onOpenChange={(open) => !open && setPendingTab(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('dirtyConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('dirtyConfirm.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('actions.keepEditing')}</AlertDialogCancel>
            <AlertDialogAction onClick={discardAndSwitch}>
              {tCommon('actions.discardChanges')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
